import Vue from 'vue'
import Vuex from 'vuex'
import { addDays, format } from 'date-fns'
import { IS_PRODUCTION, SHINHAN_RESAMPLE_START_DATE } from './libs/constants'
import { getNormalizedPlanData } from './libs/plan-helper'
import { removeAuthData } from './libs/auth-helper'
import { getUserInfo } from '@/api/user'
import { getCurrentNext } from '@/api/userPlan'
import { getBookmarkCount } from '@/api/ranking'

Vue.use(Vuex)

const now = new Date()

export const JOURNEY_TARGETING_VIEWABLE_START_DATE = '2022-06-01'

const store = new Vuex.Store({
  state: {
    apiCancelSource: null,
    errorData: null,
    globalNoData: true,
    layout: {
      gnbMinimize: false,
      lnbMinimize: false
    },
    isFirstLogin: false,
    stickyData: null,
    stickyDataId: null,
    showStickyData: false,
    showLoginAlert: false,
    showNoPvDialog: true,
    userData: null,
    planData: null,
    applyData: null,
    ctaData: {},
    showUserMenu: false,
    resizeTimestamp: 0,
    myInfoTimestamp: null,
    colorMode: 'light',
    gnbMinimized: false,
    journeyPeriod: window._journeyPeriodFromParent && window._journeyPeriodFromParent.start && window._journeyPeriodFromParent.end ? {
      start: window._journeyPeriodFromParent.start,
      end: window._journeyPeriodFromParent.end
    } : {
      start: format(addDays(now, -6), 'YYYY-MM-DD'),
      end: format(now, 'YYYY-MM-DD')
    },
    journeyOptions: null,
    journeyPluck: null,
    journeyAiReferrerProviders: [],
    aiReportData: null,
    journeyExcelData: {},
    baApplyBasketCount: 0,
    totalRemainJam: 0,

    journeyTargetingOptions: window._journeyTargetingOptionsFromParent || null,
    bookmarkListLayerOptions: null,

    numBookmarks: -1,
    numMaxBookmarks: 60,

    domainList: [],

    surveyList: false,
    surveyDetail: {},

    scrollbarWidth: 0,

    originRankingData: {},
    originJourneyData: {},
    originTrendData: {},
    basicInfo: {},

    domain: {},

    targetStartFromHead: false,

    devicelist: [],

    isSampling: false,
    isDemo: false,
    jamListFilter: [],
    showAiDocent: false
  },
  getters: {
    creditData (state) {
      const result = {
        hasPlan: false,
        canChargePv: false,
        isUnlimited: false,
        remain: 0,
        percentage: 0
      }

      const userData = state.userData
      const planData = state.planData

      if (userData && planData) {
        result.hasPlan = planData.hasPlan

        if (!userData.userCreditEmpty && planData.hasPlan) {
          result.remain = userData.remainPv

          if (result.remain === -1) {
            result.isUnlimited = true
            result.percentage = 100
          } else {
            result.percentage = userData.battery || 0
          }
        }

        result.canChargePv = planData.canChargePv
      }
      return result
    }
  },
  mutations: {
    setTargetStartFromHead (state, value) {
      state.targetStartFromHead = value
    },
    setOriginRankingData (state, value) {
      state.originRankingData = value
    },
    setOriginJourneyData (state, value) {
      state.originJourneyData = value
    },
    setOriginTrendData (state, value) {
      state.originTrendData = value
    },
    setBasicInfo (state, value) {
      state.basicInfo = value
    },
    setDomain (state, value) {
      state.domain = value
      const domain = state.domain
      state.isSampling = domain.samplingRate < 100
    },
    setApiCancelSource (state, value) {
      state.apiCancelSource = value
    },
    setUserData (state, value) {
      state.userData = value
    },
    setPlanData (state, value) {
      state.planData = value
    },
    setGlobalNoData (state, payload) {
      state.globalNoData = payload
    },
    setErrorData (state, payload) {
      state.errorData = payload
    },
    setLnbMinimize (state) {
      state.layout.lnbMinimize = !state.layout.lnbMinimize
    },
    setGnbMinimize (state) {
      state.layout.gnbMinimize = !state.layout.gnbMinimize
    },
    setIsFirstLogin (state, payload) {
      state.isFirstLogin = payload
    },
    setShowUserMenu (state, value) {
      state.showUserMenu = value
    },
    setApplyData (state, value) {
      state.applyData = value
    },
    setCtaData (state, payload) {
      state.ctaData = payload
    },
    setResizeTimestamp (state, value) {
      state.resizeTimestamp = value
    },
    setStickyData (state, payload) {
      state.stickyData = payload
    },
    setStickyDataId (state, payload) {
      state.stickyDataId = payload
    },
    setShowStickyData (state, payload) {
      state.showStickyData = payload
    },
    setShowLoginAlert (state, payload) {
      state.showLoginAlert = payload
    },
    setShowNoPvDialog (state, payload) {
      state.showNoPvDialog = payload
    },
    setMyInfoTimestamp (state, payload) {
      state.myInfoTimestamp = payload
    },
    clearAllUserData (state) {
      state.userData = null
      state.planData = null
      state.stickyData = null
      state.stickyDataId = null
      state.showStickyData = true
      state.showNoPvDialog = true
      state.myInfoTimestamp = null
    },

    setColorMode (state, value) {
      state.setColorMode = value
    },
    setDeviceList (state, value) {
      state.devicelist = value
    },
    setViewOptions (state, value) {
      state.viewOptions = value
    },
    setBaGnbMinimized (state, value) {
      state.gnbMinimized = value
    },
    setJourneyPeriod (state, value) {
      state.journeyPeriod = value
      const domain = state.domain
      // temp shinhan_test
      state.isSampling = domain.samplingRate < 100
      if (state.isSampling && domain.userId === 'shinhan_test') {
        if (value.start < SHINHAN_RESAMPLE_START_DATE && value.end >= SHINHAN_RESAMPLE_START_DATE) {
          value.start = SHINHAN_RESAMPLE_START_DATE
        }
        if (value.start >= SHINHAN_RESAMPLE_START_DATE) {
          state.isSampling = true
        } else {
          state.isSampling = false
        }
      }
    },
    setJourneyOptions (state, value) {
      state.journeyOptions = value
    },
    setJourneyPluck (state, value) {
      state.journeyPluck = value
    },
    setJourneyAiReferrerProviders (state, value) {
      state.journeyAiReferrerProviders = value || []
    },
    setAIReportData (state, value) {
      state.aiReportData = value
    },
    setJourneyExcelData (state, value) {
      state.journeyExcelData = {
        ...state.journeyExcelData,
        ...value
      }
    },

    setJourneyTargetingOptions (state, value) {
      if (value && typeof value === 'object' && !value.direction) {
        value.direction = 'backward'
      }
      state.journeyTargetingOptions = value
    },
    setBookmarkListLayerOptions (state, value) {
      state.bookmarkListLayerOptions = typeof value === 'object' ? value || {} : value
    },
    setNumBookmarks (state, value) {
      state.numBookmarks = value
    },
    setNumMaxBookmarks (state, value) {
      state.numMaxBookmarks = value
    },
    setSurveyList (state, v) {
      let sid = v[0]
      let value = v[1]
      if (value) {
        if (value.cache) {
          delete value.cache
        }
        for (let i = 0; i < value.length; i++) {
          if (!value[i]) {
            delete value[i]
          }
        }
      }
      if (state.surveyList) {
        value = {
          ...state.surveyList,
          [sid]: value
        }
      } else {
        value = {
          [sid]: value
        }
      }
      state.surveyList = value
    },
    setSurveyDetail (state, v) {
      let key = v[0]
      let value = v[1]
      state.surveyDetail[key] = value
    },

    setScrollbarWidth (state, value) {
      state.scrollbarWidth = value
    },
    setBaApplyBasketCount (state, value) {
      state.baApplyBasketCount = value
    },
    setTotalRemainJam (state, value) {
      state.totalRemainJam = value
    },
    setIsDemo (state, value) {
      state.isDemo = value
    },
    setJamListFilter (state, value) {
      state.jamListFilter = value
    },
    setShowAiDocent (state, value) {
      state.showAiDocent = value
    }
  },
  actions: {
    storeUserData ({ dispatch, commit }, payload) {
      return getUserInfo(payload).then(data => {
        commit('setUserData', data)
        return data
      })
    },
    storePlanData ({ commit, state }, payload) {
      const handlePlanData = (data) => {
        const planData = getNormalizedPlanData(data)
        let stickyDataId = null

        if (planData && planData.stickyData) {
          stickyDataId = planData.stickyData.id
          const oldStickyDataId = state.stickyDataId

          if (stickyDataId !== oldStickyDataId) {
            commit('setShowStickyData', true)
          }
        }
        commit('setStickyDataId', stickyDataId)
        commit('setPlanData', planData)

        if (payload && payload.loggingIn) {
          const now = new Date()
          if (now.getHours() <= 1) {
            if (!planData || !planData.next || format(planData.next.startDate, 'YYYY-MM-DD') === format(now, 'YYYY-MM-DD')) {
              removeAuthData()
              commit('setShowLoginAlert', true)
            }
          }
        }
        return planData
      }
      const requestData = {}
      if (state.domain.shared) {
        requestData.userId = state.domain.userId
        requestData.userNum = state.domain.userNum
      }
      return getCurrentNext(requestData, true).then(data => {
        return handlePlanData(data)
      }).catch(error => {
        if (error.message === 'No Plan found') {
          handlePlanData({})
        } else {
          throw error
        }
      })
    },

    storeBookmarkCount ({ dispatch, commit }) {
      const payload = {
        sid: store.state.journeyOptions.sid,
        isCustomNode: store.state.journeyOptions.isCustomNode,
        matchType: store.state.journeyOptions.matchType
      }

      if (store.state.journeyOptions.subSid) {
        payload.subSid = store.state.journeyOptions.subSid
      }

      if (store.state.journeyOptions.firstPath) {
        payload.firstPathname = store.state.journeyOptions.firstPath
      }

      return getBookmarkCount(payload, true)
        .then(response => {
          commit('setNumBookmarks', response.count)
        })
        .catch((error) => {
          throw error
        })
    },

    storeScrollbarWidth ({ dispatch, commit }) {
      let scrollDiv = document.createElement('div')
      scrollDiv.style.cssText = 'width: 100px; height: 100px; overflow: scroll; position: absolute; top: -9999px;'
      document.body.appendChild(scrollDiv)

      let scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth
      commit('setScrollbarWidth', scrollbarWidth)

      document.body.removeChild(scrollDiv)
    }
  },
  strict: IS_PRODUCTION
})

export default store

export function getStore () {
  return store
}
